import { useQuery } from '@tanstack/react-query';
import type { RequestOptions } from '@/api/client';
import { keys } from '@/api/queryKeys';
import { enrichWithCachedSentAttachments } from '../adapters/sentAttachmentCache';
import { emailApi } from '../api/emailApi';
import type { EmailMessageDetail } from '../api/types';

async function fetchEmailThreadDetail(
  id: string,
  options?: RequestOptions,
): Promise<EmailMessageDetail> {
  const [legacy, fd12] = await Promise.allSettled([
    emailApi.getLegacyThread(id, undefined, options),
    emailApi.getMessage(id, options),
  ]);

  if (legacy.status === 'fulfilled') {
    let detail = legacy.value;
    if (fd12.status === 'fulfilled') {
      const receipts = fd12.value;
      const mergedMessages = detail.messages?.map((message) => {
        const receiptRow = receipts.messages?.find((row) => row.id === message.id);
        if (!receiptRow) return message;
        return {
          ...message,
          ...(receiptRow.seen_by?.length ? { seen_by: receiptRow.seen_by } : {}),
          ...(receiptRow.seen_count != null ? { seen_count: receiptRow.seen_count } : {}),
        };
      });

      detail = {
        ...detail,
        ...(receipts.seen_by?.length ? { seen_by: receipts.seen_by } : {}),
        ...(receipts.seen_count != null ? { seen_count: receipts.seen_count } : {}),
        ...(mergedMessages ? { messages: mergedMessages } : {}),
      };
    }
    return enrichWithCachedSentAttachments(detail);
  }

  if (fd12.status === 'fulfilled') return enrichWithCachedSentAttachments(fd12.value);
  throw legacy.reason;
}

export function useEmailMessage(id: string | null | undefined) {
  return useQuery({
    queryKey: keys.email.message(id ?? ''),
    queryFn: ({ signal }) => fetchEmailThreadDetail(id!, { signal }),
    enabled: id != null,
    staleTime: 60_000,
  });
}

export { useReplySend, useApproveDraft, usePatchEmailMessage, useTranslateThread } from './useEmailMutations';
